
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useTenant } from './providers/TenantProvider';
import { defaultAuthenticatedPath, hasAdminAccess, hasMemberAccess, tenantNeedsBillingAttention, tenantNeedsOnboarding } from './lib/tenantRouting';
import Layout from './components/Layout';
import MemberPortalLayout from './components/MemberPortalLayout';
import { features } from './features';
import type { MenuFeature } from './lib/planFeatures';
import { canAccessMenuFeature } from './lib/planFeatures';

// Pages
import { AuthPage } from './pages/AuthPage';
import { DashboardPage } from './pages/DashboardPage';
import { SignupPage } from './pages/SignupPage';
import { PlansPage } from './pages/PlansPage';
import { SettingsBillingPage } from './pages/SettingsBillingPage';
import { GeneralSettingsPage } from './pages/settings/GeneralSettingsPage';
import { BookingSettingsPage } from './pages/settings/BookingSettingsPage';
import { BusinessLogicPage } from './pages/settings/BusinessLogicPage';
import { MembersListPage } from './pages/MembersListPage';
import { MemberProfilePage } from './pages/MemberProfilePage';
import { CheckInPage } from './pages/CheckInPage';
import { WaiversPage } from './pages/WaiversPage';
import { SetupPage } from './pages/SetupPage';
import LeadsPipelineBoard from './pages/LeadsPipelineBoard';
import LeadDetailPage from './pages/LeadDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import ReportsPage from './pages/ReportsPage';
import { UsersPage } from './pages/UsersPage';
import { ServicesPage } from './pages/ServicesPage';
import { ProviderSchedulePage } from './pages/ProviderSchedulePage';
import { ServiceFormPage } from './pages/ServiceFormPage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { WebsiteDashboardPage } from './pages/WebsiteDashboardPage';
import { WebsiteOnboardingPage } from './pages/WebsiteOnboardingPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { CheckoutCancelledPage } from './pages/CheckoutCancelledPage';
import { OnboardingPage } from './pages/OnboardingPage';
import UiKitPage from './pages/UiKitPage';
import { BookingPage } from './pages/public/BookingPage';
import { BookingProviderPage } from './pages/public/BookingProviderPage';
import { BookingTimePage } from './pages/public/BookingTimePage';
import { BookingContactPage } from './pages/public/BookingContactPage';
import { AppointmentManagePage } from './pages/public/AppointmentManagePage';
import { WaiverPage } from './pages/public/WaiverPage';
import { MemberDashboardPage } from './pages/member/MemberDashboardPage';
import { MemberBookPage } from './pages/member/MemberBookPage';
import { MemberAppointmentsPage } from './pages/member/MemberAppointmentsPage';
function App() {
  const { loading, user, tenantId, roles, activeTenant } = useTenant();
  const isAuthenticated = !!user;
  const hasTenantAccess = !!tenantId;
  const adminUser = hasAdminAccess(roles);
  const memberUser = hasMemberAccess(roles);
  const ownerUser = roles.includes('owner');
  const nextAuthenticatedPath = defaultAuthenticatedPath(activeTenant, roles);

  const adminRoute = (
    element: any,
    options: {
      allowPendingBilling?: boolean;
      allowIncompleteOnboarding?: boolean;
      allowMissingTenant?: boolean;
      ownerOnly?: boolean;
      feature?: MenuFeature;
    } = {}
  ) => {
    if (!isAuthenticated) return <Navigate to="/" replace />;
    if (!hasTenantAccess) {
      return options.allowMissingTenant ? <Layout>{element}</Layout> : <Navigate to="/signup" replace />;
    }
    if (!adminUser) return <Navigate to={memberUser ? '/member' : '/signup'} replace />;
    if (options.ownerOnly && !ownerUser) return <Navigate to={nextAuthenticatedPath} replace />;
    if (options.feature && !canAccessMenuFeature(activeTenant?.planId, options.feature)) {
      return <Navigate to={nextAuthenticatedPath} replace />;
    }
    if (!options.allowPendingBilling && tenantNeedsBillingAttention(activeTenant)) {
      return <Navigate to="/checkout/cancelled" replace />;
    }
    if (!options.allowIncompleteOnboarding && tenantNeedsOnboarding(activeTenant, roles)) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Layout>{element}</Layout>;
  };

  const memberRoute = (element: any) => {
    if (!isAuthenticated) return <Navigate to="/" replace />;
    if (!memberUser) return <Navigate to={adminUser ? nextAuthenticatedPath : '/signup'} replace />;
    return <MemberPortalLayout>{element}</MemberPortalLayout>;
  };

  const ownerPortalRoute = (
    element: any,
    options: { allowPendingBilling?: boolean; withLayout?: boolean } = {}
  ) => {
    if (!isAuthenticated) return <Navigate to="/" replace />;
    if (!hasTenantAccess) return <Navigate to="/signup" replace />;
    if (!ownerUser) return <Navigate to={nextAuthenticatedPath} replace />;
    if (!options.allowPendingBilling && tenantNeedsBillingAttention(activeTenant)) {
      return <Navigate to="/checkout/cancelled" replace />;
    }
    return options.withLayout ? <Layout>{element}</Layout> : element;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Auth Route */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <AuthPage />}
      />
      <Route path="/signup" element={<SignupPage />} />
      <Route
        path="/checkout/success"
        element={isAuthenticated ? <CheckoutSuccessPage /> : <Navigate to="/" replace />}
      />
      <Route
        path="/checkout/cancelled"
        element={ownerPortalRoute(<CheckoutCancelledPage />, { allowPendingBilling: true })}
      />
      <Route
        path="/onboarding"
        element={ownerPortalRoute(<OnboardingPage />, { withLayout: true })}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={adminRoute(<DashboardPage />, { allowMissingTenant: true })}
      />
      <Route
        path="/plans"
        element={adminRoute(<PlansPage />)}
      />
      <Route
        path="/settings/billing"
        element={<Navigate to="/settings/payment" replace />}
      />
      <Route
        path="/settings"
        element={<Navigate to="/settings/general" replace />}
      />
      <Route
        path="/settings/general"
        element={adminRoute(<GeneralSettingsPage />)}
      />
      <Route
        path="/settings/booking"
        element={adminRoute(<BookingSettingsPage />)}
      />
      <Route
        path="/settings/business"
        element={adminRoute(<BusinessLogicPage />)}
      />
      <Route
        path="/settings/payment"
        element={adminRoute(<SettingsBillingPage />)}
      />
      <Route
        path="/members"
        element={adminRoute(<MembersListPage />)}
      />
      <Route
        path="/waivers"
        element={adminRoute(<WaiversPage />)}
      />
      <Route
        path="/members/:memberId"
        element={adminRoute(<MemberProfilePage />)}
      />
      <Route
        path="/checkin"
        element={adminRoute(<CheckInPage />)}
      />
      <Route
        path="/setup"
        element={isAuthenticated ? <SetupPage /> : <Navigate to="/" replace />}
      />
      <Route
        path="/leads"
        element={adminRoute(<LeadsPipelineBoard />, { feature: 'leads' })}
      />
      <Route
        path="/leads/:leadId"
        element={adminRoute(<LeadDetailPage />, { feature: 'leads' })}
      />
      <Route
        path="/invoices"
        element={adminRoute(<InvoicesPage />)}
      />
      <Route
        path="/reports"
        element={adminRoute(<ReportsPage />)}
      />
      <Route
        path="/bookings"
        element={<Navigate to="/appointments" replace />}
      />
      <Route
        path="/services"
        element={adminRoute(<ServicesPage />)}
      />
      <Route
        path="/services/new"
        element={adminRoute(<ServiceFormPage />)}
      />
      <Route
        path="/services/:id/edit"
        element={adminRoute(<ServiceFormPage />)}
      />
      <Route
        path="/users"
        element={adminRoute(<UsersPage />)}
      />
      <Route
        path="/website"
        element={adminRoute(<WebsiteDashboardPage />, { ownerOnly: true })}
      />
      <Route
        path="/website/onboarding"
        element={adminRoute(<WebsiteOnboardingPage />, { ownerOnly: true })}
      />
      <Route
        path="/schedules/:providerId"
        element={adminRoute(<ProviderSchedulePage />)}
      />
      <Route
        path="/appointments"
        element={adminRoute(<AppointmentsPage />)}
      />
      {features.uiKit && (
        <Route
          path="/ui-kit"
          element={adminRoute(<UiKitPage />)}
        />
      )}

      {/* Member Portal Routes - Auth required */}
      <Route
        path="/member"
        element={memberRoute(<MemberDashboardPage />)}
      />
      <Route
        path="/member/book"
        element={memberRoute(<MemberBookPage />)}
      />
      <Route
        path="/member/appointments"
        element={memberRoute(<MemberAppointmentsPage />)}
      />

      {/* Public Booking Routes - No auth required */}
      <Route path="/book/:tenantSlug" element={<BookingPage />} />
      <Route path="/book/:tenantSlug/service/:serviceId" element={<BookingProviderPage />} />
      <Route path="/book/:tenantSlug/service/:serviceId/provider/:providerId" element={<BookingTimePage />} />
      <Route path="/book/:tenantSlug/service/:serviceId/provider/:providerId/contact" element={<BookingContactPage />} />
      <Route path="/waiver/:tenantSlug" element={<WaiverPage />} />
      <Route path="/appointment/:publicAccessHash" element={<AppointmentManagePage />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to={isAuthenticated ? nextAuthenticatedPath : '/'} replace />} />
    </Routes>
  );
}

export default App;
